import React from 'react';

// Paramount palette
const OLIVE = '#3d4128';
const SAGE = '#b9c096';
const CREAM = '#fbf8f3';

export const QUEUE_META = {
  needs_action:  { label: 'Needs Action',   icon: '\u26a1',           color: '#9a3412', bg: '#fff7ed', border: '#fdba74', desc: 'A prescription exists \u2014 execute it' },
  losing_ground: { label: 'Losing Ground',  icon: '\ud83d\udcc9',     color: '#991b1b', bg: '#fef2f2', border: '#fca5a5', desc: 'Dropped 5+ spots vs prior period' },
  quick_wins:    { label: 'Quick Wins',     icon: '\ud83d\ude80',     color: '#1e40af', bg: '#eff6ff', border: '#93c5fd', desc: 'Position 4\u201315 with clicks \u2014 within reach' },
  winning:       { label: 'Winning',        icon: '\ud83c\udfc6',     color: '#166534', bg: '#f0fdf4', border: '#86efac', desc: 'Top 3 with clicks \u2014 defend' },
  questions:     { label: 'Questions',      icon: '\u2753',           color: '#6b21a8', bg: '#faf5ff', border: '#c4b5fd', desc: 'Question queries \u2014 content ideas' },
  brand:         { label: 'Brand',          icon: '\ud83c\udff7\ufe0f', color: OLIVE,   bg: CREAM,     border: SAGE,      desc: 'Searchers looking for this site by name' },
  watching:      { label: 'Watching',       icon: '\ud83d\udc41\ufe0f', color: '#475569', bg: '#f8fafc', border: '#cbd5e1', desc: 'Has signal, nothing actionable yet' },
};

const QUEUE_ORDER = ['needs_action', 'losing_ground', 'quick_wins', 'winning', 'questions', 'brand', 'watching'];

const BUCKETS = [
  { key: 'top3',   label: 'Top 3',  color: '#10b981', desc: '1\u20133' },
  { key: 'page1',  label: 'Page 1', color: '#3b82f6', desc: '4\u201310' },
  { key: 'page2',  label: 'Page 2', color: '#b98a3b', desc: '11\u201320' },
  { key: 'page3',  label: 'Page 3', color: '#c2683f', desc: '21\u201350' },
  { key: 'beyond', label: 'Beyond', color: '#94a3b8', desc: '51+' },
];

function Delta({ value, invert, suffix }) {
  if (value === null || value === undefined) return null;
  const good = invert ? value < 0 : value > 0;
  const zero = value === 0;
  const color = zero ? '#94a3b8' : good ? '#16a34a' : '#dc2626';
  const arrow = zero ? '\u2192' : value > 0 ? '\u2191' : '\u2193';
  return (
    <span style={{ fontSize: 12, fontWeight: 700, color }}>
      {arrow} {Math.abs(value)}{suffix || ''}
    </span>
  );
}

function StatCard({ label, value, delta, invert, suffix, note }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', minWidth: 150, flex: '1 1 150px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 24, fontWeight: 800, color: OLIVE, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
        <Delta value={delta} invert={invert} suffix={suffix} />
      </div>
      {note && <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>{note}</div>}
    </div>
  );
}

export default function QueueHeader({ stats, days, activeBucket, onBucketClick, activeQueue, onQueueClick }) {
  if (!stats) return null;

  const dist = stats.distribution || {};
  const prior = stats.prior_distribution || {};
  const queues = stats.queues || {};
  const topline = stats.topline || {};
  const toplineDelta = stats.topline_delta || {};

  const bucketCount = k => (dist[k] && dist[k].count) || 0;
  const bucketDelta = k => (prior[k] !== undefined ? bucketCount(k) - (prior[k] || 0) : null);

  const page1Now = bucketCount('top3') + bucketCount('page1');
  const page1Prior = (prior.top3 || 0) + (prior.page1 || 0);
  const totalSignal = dist.total || 0;

  return (
    <div style={{ marginBottom: 18 }}>
      {/* ── Verdict cards ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <StatCard
          label="Page 1 Keywords"
          value={page1Now}
          delta={stats.prior_distribution ? page1Now - page1Prior : null}
          note={`of ${totalSignal} with signal`}
        />
        <StatCard
          label="Clicks"
          value={(topline.clicks || 0).toLocaleString()}
          delta={toplineDelta.clicks_pct}
          suffix="%"
          note={`last ${days} days vs prior`}
        />
        <StatCard
          label="New Arrivals"
          value={stats.new_arrivals !== undefined ? stats.new_arrivals : '\u2014'}
          note="keywords that gained signal this period"
        />
        <StatCard
          label="Losing Ground"
          value={queues.losing_ground || 0}
          note="dropped 5+ spots, had clicks before"
        />
      </div>

      {/* ── Position bar (signal keywords only) ───────────────────── */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', marginBottom: 14 }}>
        <div style={{ display: 'flex', height: 22, borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
          {BUCKETS.map(b => {
            const n = bucketCount(b.key);
            if (!n || !totalSignal) return null;
            const active = activeBucket === b.key;
            return (
              <div
                key={b.key}
                title={`${b.label} (${b.desc}): ${n}`}
                onClick={() => onBucketClick && onBucketClick(b.key)}
                style={{
                  width: `${(n / totalSignal) * 100}%`,
                  background: b.color,
                  minWidth: 4,
                  cursor: 'pointer',
                  opacity: activeBucket && !active ? 0.35 : 1,
                  transition: 'opacity 0.15s',
                }}
              />
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
          {BUCKETS.map(b => {
            const d = bucketDelta(b.key);
            const active = activeBucket === b.key;
            return (
              <span
                key={b.key}
                onClick={() => onBucketClick && onBucketClick(b.key)}
                style={{ color: active ? OLIVE : '#374151', fontWeight: active ? 800 : 500, cursor: 'pointer' }}
              >
                <span style={{ display: 'inline-block', width: 9, height: 9, background: b.color, borderRadius: 2, marginRight: 5 }} />
                {b.label}: <b style={{ fontVariantNumeric: 'tabular-nums' }}>{bucketCount(b.key)}</b>
                {d !== null && d !== 0 && (
                  <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 700, color: d > 0 ? '#16a34a' : '#dc2626' }}>
                    {d > 0 ? '+' : ''}{d}
                  </span>
                )}
              </span>
            );
          })}
          {(dist.low_signal_count || 0) > 0 && (
            <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: 11.5 }}>
              {dist.low_signal_count.toLocaleString()} low-signal excluded
            </span>
          )}
        </div>
      </div>

      {/* ── Queue rail ────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginRight: 2 }}>
          Queues:
        </span>
        {QUEUE_ORDER.map(q => {
          const meta = QUEUE_META[q];
          const n = queues[q] || 0;
          if (!n) return null;
          const active = activeQueue === q;
          return (
            <button
              key={q}
              title={meta.desc}
              onClick={() => onQueueClick && onQueueClick(q)}
              style={{
                fontSize: 12.5,
                fontWeight: active ? 800 : 600,
                padding: '5px 12px',
                borderRadius: 18,
                border: `1.5px solid ${active ? meta.color : meta.border}`,
                background: active ? meta.color : meta.bg,
                color: active ? '#fff' : meta.color,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span>{meta.icon}</span>
              <span>{meta.label}</span>
              <span style={{
                fontSize: 11, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                padding: '1px 7px', borderRadius: 10,
                background: active ? 'rgba(255,255,255,0.25)' : '#fff',
                color: active ? '#fff' : meta.color,
              }}>
                {n}
              </span>
            </button>
          );
        })}
        {activeQueue && (
          <button
            onClick={() => onQueueClick && onQueueClick(activeQueue)}
            style={{ fontSize: 12, padding: '4px 10px', borderRadius: 18, border: '1.5px solid #fca5a5', background: '#fef2f2', color: '#ef4444', cursor: 'pointer', fontWeight: 600 }}
          >
            {'\u2715'} Clear queue
          </button>
        )}
      </div>
    </div>
  );
}
